import { Typography } from '@euler/app/components/typography';
import { SiteIcon } from '@euler/app/flows/task/components/SiteIcon';
import { SiteInspectionManager } from '@euler/app/flows/task/functions/task-detail/inspection/SiteInspectionManager';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AbnormalLevel } from '@euler/model/enum';
import { PreInspection } from '@euler/model/task-detail';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { FC, memo, useCallback } from 'react';
import { TouchableHighlight } from 'react-native';

export type TroubleLightItem = {
  siteId: number;
  siteCode: string;
  name: string;
  icon?: string;
};

export const TroubleLightCell: FC<{
  siteId: number;
  siteCode: string;
  name: string;
  icon?: string;
  checked?: boolean;
  readonly?: boolean;
  onPress?: (siteId: number, siteCode: string) => void;
}> = memo(({ siteId, siteCode, name, icon, checked, readonly, onPress }) => {
  const { rpx } = useResponsiveContext();

  const onCellPress = useCallback(() => {
    onPress?.(siteId, siteCode);
  }, [siteId, siteCode, onPress]);

  return (
    <TouchableHighlight
      onPress={readonly ? undefined : onCellPress}
      activeOpacity={readonly ? 1 : 0.75}
      underlayColor={'#eee'}
    >
      <HStack
        css={`
          height: ${rpx(44)}px;
          margin-top: ${rpx(10)}px;
          padding: 0 ${rpx(15)}px;
          border-bottom-width: 1px;
          border-bottom-color: #f1f1f1;
        `}
      >
        <HStack
          justifyContent="flex-start"
          css={`
            flex: 1;
          `}
        >
          <SiteIcon
            url={icon}
            type={icon ? (icon.includes('.svg') ? 'svg' : 'image') : undefined}
            size={rpx(26)}
            color="#888"
            css={`
              margin-right: ${rpx(15)}px;
            `}
          />
          <Typography.Label light size={rpx(16)}>
            {name}
          </Typography.Label>
        </HStack>
        {!readonly || checked ? (
          <Icon
            name={
              checked ? (readonly ? 'check' : 'checkbox') : 'checkbox_outline'
            }
            size={rpx(24)}
            color={checked ? 'rgba(32, 127, 231, 1)' : 'rgba(241, 241, 241, 1)'}
          />
        ) : null}
      </HStack>
    </TouchableHighlight>
  );
});

export const WiredTroubleLightCell = memo(
  ({
    item,
    siteInspectionManager,
    onSiteCheckChange,
  }: {
    item: TroubleLightItem;
    siteInspectionManager: SiteInspectionManager<PreInspection>;
    onSiteCheckChange: (siteId: number, isDefective: boolean) => void;
  }) => {
    const [staged] = useBehaviorSubject(siteInspectionManager.staged$);
    const checked = staged.items.some(
      x => x.abnormalLevel !== AbnormalLevel.Fine,
    );
    const onPress = useCallback(() => {
      onSiteCheckChange(item.siteId, !checked);
    }, [checked, item.siteId, onSiteCheckChange]);
    return <TroubleLightCell {...item} checked={checked} onPress={onPress} />;
  },
);
